"use client";

/**
 * Wallet search + report intake.
 *
 * The page had no search on it. Its own metadata (app/search/layout.tsx)
 * promises "Search any blockchain wallet to see its trust score, flags,
 * reports, and community reputation across 75+ chains", but every control was
 * report intake: owner wallet, chain, provider attribution, target address.
 * Meanwhile `/api/search` resolves ENS and Unstoppable domains, auto-detects
 * the best EVM chain by transaction count, and handles transaction hashes —
 * and `WalletSearchBar` already consumes it from the navbar and the homepage
 * hero, but not here. Search is now the primary surface.
 *
 * The report intake is preserved in full below the results: ReportModal is
 * used nowhere else in the app, so this remains its only home. Every handler
 * (openReport, signAndVerify, provider selection, AddProviderModal,
 * ReportModal's onSubmitted redirect) behaves as before.
 *
 * Other fixes in this pass:
 *
 *  - The hero duplicated two of the form's four actions — "Flag or report an
 *    address" and "Verify my wallet" called the same handlers as "Flag /
 *    report address" and "Sign & verify my wallet" further down — so the page
 *    offered six buttons for four distinct actions. Deduplicated.
 *
 *  - "Analyze address" pushed straight to /wallet/{chain}/{address} with no
 *    validation, so a typo navigated to a wallet route that then failed. It
 *    now validates per chain family first.
 *
 *  - The page ran `neutralizeStaleOverlays()` on mount, walking every direct
 *    child of <body> and setting `pointer-events: none` on any fixed,
 *    full-screen element with z-index above 10000 that lacked a `data-test-id`
 *    attribute — a workaround for a stale navbar drawer swallowing clicks.
 *    Nothing on the page trips it today (the auth modal is z-5000, the
 *    provider dropdown z-1200), but it would silently disable any future
 *    overlay above that threshold. Replaced with a targeted reset of the
 *    state the drawer actually leaves behind.
 *
 *  - Colours were hard-coded (`backgroundColor: "#020617"`, `color:
 *    "#e5e7eb"`, `text-slate-50`), so the page ignored the app's theme toggle.
 *    It now uses the shared TrustSurface tokens, defined for both themes.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import ProviderSelect from 'app/components/ProviderSelect';
import AddProviderModal from 'app/components/AddProviderModal';
import ReportModal from 'app/components/ReportModal';
import { openAuthModal } from '../components/auth/openAuthModal';
import { WalletAvatar } from '../components/WalletIdenticon';
import Footer from '../components/Footer';
import TrustSurface from '../components/ui/TrustSurface';

/* ────────────────────────────────────────────────────────────────────────── */
/* Types                                                                      */
/* ────────────────────────────────────────────────────────────────────────── */

type ProviderType = { _id?: string; name: string; website?: string; type?: string; rank?: number; status?: string };

interface SearchProfile {
  address: string;
  chain: string;
  ens?: string;
  avgRating?: number;
  nftCount?: number;
  blacklisted?: boolean;
  blacklistReason?: string;
  flagsCount?: number;
  flagsSummary?: string[];
  kycStatus?: string;
  socials?: { displayName?: string; avatarUrl?: string };
  trustScore?: number;
  riskScore?: number;
  statusTags?: string[];
}

interface TxResult {
  hash?: string;
  chain?: string;
  from?: string;
  to?: string;
  value?: string | number;
  blockNumber?: number | string;
}

type SearchState =
  | { kind: 'idle' }
  | { kind: 'loading' }
  | { kind: 'error'; message: string }
  | { kind: 'wallets'; results: SearchProfile[] }
  | { kind: 'tx'; tx: TxResult | null };

/* ────────────────────────────────────────────────────────────────────────── */
/* Chains + validation                                                        */
/* ────────────────────────────────────────────────────────────────────────── */

type ChainFamily = 'evm' | 'bitcoin' | 'solana' | 'tron' | 'xrp';

/** Kept in step with `allowedChains` in services/wallet/getWalletData.ts. */
const CHAINS: Array<{ value: string; label: string; family: ChainFamily }> = [
  { value: 'ethereum', label: 'Ethereum', family: 'evm' },
  { value: 'base', label: 'Base', family: 'evm' },
  { value: 'arbitrum', label: 'Arbitrum', family: 'evm' },
  { value: 'optimism', label: 'Optimism', family: 'evm' },
  { value: 'polygon', label: 'Polygon', family: 'evm' },
  { value: 'bsc', label: 'BNB Smart Chain', family: 'evm' },
  { value: 'avalanche', label: 'Avalanche', family: 'evm' },
  { value: 'bitcoin', label: 'Bitcoin', family: 'bitcoin' },
  { value: 'solana', label: 'Solana', family: 'solana' },
  { value: 'tron', label: 'Tron', family: 'tron' },
  { value: 'xrp', label: 'XRP Ledger', family: 'xrp' },
];

const FAMILY_BY_CHAIN = new Map(CHAINS.map((c) => [c.value, c.family]));

const ADDRESS_TEST: Record<ChainFamily, (v: string) => boolean> = {
  evm: (v) => /^0x[a-fA-F0-9]{40}$/.test(v),
  bitcoin: (v) => /^(bc1[a-z0-9]{11,71}|[13][a-km-zA-HJ-NP-Z1-9]{25,34})$/.test(v),
  solana: (v) => /^[1-9A-HJ-NP-Za-km-z]{32,44}$/.test(v),
  tron: (v) => /^T[1-9A-HJ-NP-Za-km-z]{33}$/.test(v),
  xrp: (v) => /^r[1-9A-HJ-NP-Za-km-z]{24,34}$/.test(v),
};

const ADDRESS_HINT: Record<ChainFamily, string> = {
  evm: 'EVM addresses start with 0x and are 42 characters long.',
  bitcoin: 'Bitcoin addresses start with 1, 3 or bc1.',
  solana: 'Solana addresses are 32–44 base58 characters.',
  tron: 'Tron addresses start with T and are 34 characters long.',
  xrp: 'XRP Ledger addresses start with r.',
};

function shortAddress(a?: string) {
  if (!a) return '';
  return a.length > 16 ? `${a.slice(0, 8)}…${a.slice(-6)}` : a;
}

function trustTone(score?: number): 'ok' | 'warn' | 'danger' | undefined {
  if (typeof score !== 'number' || !Number.isFinite(score)) return undefined;
  if (score >= 67) return 'ok';
  if (score >= 34) return 'warn';
  return 'danger';
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Page                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */

export default function SearchPage() {
  const router = useRouter();

  // ── Search ──
  const [query, setQuery] = useState('');
  const [searchChain, setSearchChain] = useState('');
  const [search, setSearch] = useState<SearchState>({ kind: 'idle' });
  const searchIdRef = useRef(0);

  // ── Report intake (behaviour preserved) ──
  const [myWallet, setMyWallet] = useState('');
  const [chain, setChain] = useState('ethereum');
  const [provider, setProvider] = useState<ProviderType | null>(null);
  const [pendingProviderName, setPendingProviderName] = useState<string | null>(null);
  const [suspect, setSuspect] = useState('');
  const [addingProvider, setAddingProvider] = useState(false);
  const [openReportModal, setOpenReportModal] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [authenticated, setAuthenticated] = useState(false);
  const reportRef = useRef<HTMLDetailsElement | null>(null);

  // Read localStorage in an effect, not during render, to avoid a hydration mismatch.
  const [showProviderFeatures, setShowProviderFeatures] = useState<boolean>(false);
  useEffect(() => {
    try {
      const val = localStorage.getItem('showProviderFeatures');
      setShowProviderFeatures(val === null ? true : val !== 'false');
    } catch {
      // ignore
    }
  }, []);

  /**
   * FIX: replaces `neutralizeStaleOverlays()`, which walked every direct child
   * of <body> and disabled pointer events on anything fixed, full-screen and
   * above z-index 10000 without a `data-test-id`. That is a trap for any
   * overlay added later. The problem it papered over is the navbar drawer
   * leaving `#content` inert and the body scroll-locked when it unmounts
   * mid-navigation, so only that specific state is undone.
   */
  const restoreMainInteractivity = useCallback(() => {
    try {
      const main = document.getElementById('content');
      if (main) {
        main.removeAttribute('aria-hidden');
        main.removeAttribute('inert');
        main.style.pointerEvents = '';
        main.style.userSelect = '';
        main.style.touchAction = '';
      }
      if (document.body.style.overflow === 'hidden') document.body.style.overflow = '';
    } catch {
      // ignore
    }
  }, []);
  useEffect(() => {
    restoreMainInteractivity();
  }, [restoreMainInteractivity]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const statusRes = await fetch('/api/auth/status', { credentials: 'include', cache: 'no-store' });
        const status = await statusRes.json().catch(() => ({} as { authenticated?: boolean }));
        if (!cancelled) setAuthenticated(Boolean(status?.authenticated));
      } catch {
        // ignore
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  /* ── Search ───────────────────────────────────────────────────────────── */

  const runSearch = useCallback(async (q: string, chainFilter: string) => {
    const term = q.trim();
    if (!term) return;
    const id = ++searchIdRef.current;
    setSearch({ kind: 'loading' });
    try {
      const params = new URLSearchParams({ q: term });
      if (chainFilter) params.set('chain', chainFilter);
      const res = await fetch(`/api/search?${params.toString()}`, { cache: 'no-store' });
      if (id !== searchIdRef.current) return;

      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        setSearch({
          kind: 'error',
          message:
            res.status === 429
              ? 'Too many searches from this network right now. Wait a moment and try again.'
              : body?.message || `Search failed (${res.status}).`,
        });
        return;
      }

      const data = (await res.json()) as { kind?: string; tx?: TxResult | null; results?: SearchProfile[] };
      if (id !== searchIdRef.current) return;

      if (data.kind === 'tx') {
        setSearch({ kind: 'tx', tx: data.tx ?? null });
        return;
      }
      setSearch({ kind: 'wallets', results: Array.isArray(data.results) ? data.results : [] });
    } catch {
      if (id !== searchIdRef.current) return;
      setSearch({ kind: 'error', message: 'Could not reach search. Check your connection and try again.' });
    }
  }, []);

  function handleSearchSubmit(e: React.FormEvent) {
    e.preventDefault();
    void runSearch(query, searchChain);
  }

  /** Prefill the report intake from a result and bring it into view. */
  function reportFromResult(p: SearchProfile) {
    setSuspect(p.address);
    if (FAMILY_BY_CHAIN.has(p.chain)) setChain(p.chain);
    setReportOpen(true);
    setErrorMessage(null);
    setStatusMessage(null);
    window.requestAnimationFrame(() => {
      reportRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }

  /* ── Report intake handlers (unchanged) ───────────────────────────────── */

  function signAndVerify() {
    if (authenticated) {
      setErrorMessage(null);
      setStatusMessage('Your wallet session is already verified and ready for trust actions.');
      return;
    }
    openAuthModal({
      title: 'Verify wallet',
      message: 'Verify your wallet to unlock reporting, flagging, rating, and follow actions.',
      redirectTo: typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
      ctaLabel: 'Continue to sign in',
    });
  }

  function openReport() {
    setErrorMessage(null);
    setStatusMessage(null);
    if (!authenticated) {
      openAuthModal({
        title: 'Sign in required',
        message: 'Reporting and flagging require wallet verification.',
        redirectTo: typeof window !== 'undefined' ? window.location.pathname + window.location.search : undefined,
      });
      return;
    }
    // Check the address here, the way "Analyse address" already does, so a
    // blank or malformed entry is caught at the button instead of surfacing
    // later as a server-side rejection inside the confirmation step.
    const address = suspect.trim();
    if (!address) {
      setErrorMessage('Enter the receiving address you want to report first.');
      return;
    }
    const family = FAMILY_BY_CHAIN.get(chain) ?? 'evm';
    if (!ADDRESS_TEST[family](address)) {
      setErrorMessage(`That does not look like a valid ${chain} address. ${ADDRESS_HINT[family]}`);
      return;
    }
    setOpenReportModal(true);
  }

  /**
   * FIX: this pushed to /wallet/{chain}/{address} with no validation at all, so
   * a mistyped address navigated to a wallet route that then failed to resolve.
   */
  function analyzeAddress() {
    const address = suspect.trim();
    if (!address) {
      setStatusMessage(null);
      setErrorMessage('Enter a receiving address to open its wallet analysis.');
      return;
    }
    const family = FAMILY_BY_CHAIN.get(chain) ?? 'evm';
    if (!ADDRESS_TEST[family](address)) {
      setStatusMessage(null);
      setErrorMessage(`That does not look like a valid ${chain} address. ${ADDRESS_HINT[family]}`);
      return;
    }
    setErrorMessage(null);
    setStatusMessage('Opening wallet analysis…');
    router.push(`/wallet-analysis?chain=${encodeURIComponent(chain)}&address=${encodeURIComponent(address)}`);
  }

  function resetIntake() {
    setMyWallet('');
    setSuspect('');
    setProvider(null);
    setErrorMessage(null);
    setStatusMessage('Intake fields cleared.');
  }

  const results = search.kind === 'wallets' ? search.results : [];
  const resultCount = results.length;

  /* ── Render ───────────────────────────────────────────────────────────── */

  return (
    <div className="ts-root flex min-h-screen flex-col">
      <TrustSurface />

      <main className="w-full flex-1 pb-14">
        <div className="mx-auto w-full max-w-5xl px-4 pt-24 sm:px-6 sm:pt-28">
          <header className="mb-7">
            <span className="ts-chip" data-tone="accent">
              Trust operations
            </span>
            <h1 className="ts-h1 mt-5">Search any wallet</h1>
            <p className="ts-lede mt-4 max-w-2xl">
              Look up an address, an ENS or Unstoppable domain, or a transaction hash. See trust score, flags
              and community reputation before you send — then report anything that looks wrong.
            </p>
          </header>

          {/* ── Search ─────────────────────────────────────────────────── */}
          <section className="ts-card p-5 sm:p-7" aria-labelledby="ts-search-heading">
            <h2 id="ts-search-heading" className="sr-only">
              Search wallets and transactions
            </h2>
            <form onSubmit={handleSearchSubmit} noValidate>
              <div className="ts-search">
                <div className="ts-search-field">
                  <label className="ts-label" htmlFor="ts-q">
                    Address, domain or transaction
                  </label>
                  <input
                    id="ts-q"
                    className="ts-input ts-input-lg ts-mono"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="0x… · vitalik.eth · yourname.crypto"
                    autoComplete="off"
                    autoCapitalize="off"
                    spellCheck={false}
                  />
                </div>
                <div>
                  <label className="ts-label" htmlFor="ts-chain-filter">
                    Chain
                  </label>
                  <select
                    id="ts-chain-filter"
                    className="ts-select"
                    value={searchChain}
                    onChange={(e) => setSearchChain(e.target.value)}
                  >
                    <option value="">Auto-detect</option>
                    {CHAINS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="submit"
                  className="ts-btn ts-btn-primary"
                  disabled={!query.trim() || search.kind === 'loading'}
                >
                  {search.kind === 'loading' ? 'Searching…' : 'Search'}
                </button>
              </div>
              <p className="ts-hint">
                Leave the chain on auto-detect and we&apos;ll pick the network where the address is actually
                active.
              </p>
            </form>
          </section>

          {/* ── Results ────────────────────────────────────────────────── */}
          <div className="mt-6" aria-live="polite">
            {search.kind === 'loading' ? (
              <div className="grid gap-3">
                <div className="ts-skel" style={{ height: 92 }} />
                <div className="ts-skel" style={{ height: 92 }} />
                <div className="ts-skel" style={{ height: 92 }} />
              </div>
            ) : null}

            {search.kind === 'error' ? (
              <div className="ts-alert" data-tone="danger" role="alert">
                <span className="min-w-0 flex-1">{search.message}</span>
                <button type="button" className="ts-btn ts-btn-sm" onClick={() => void runSearch(query, searchChain)}>
                  Try again
                </button>
              </div>
            ) : null}

            {search.kind === 'tx' ? (
              search.tx ? (
                <section className="ts-card p-5 sm:p-6">
                  <span className="ts-chip" data-tone="accent">
                    Transaction
                  </span>
                  <p className="ts-mono mt-4" style={{ fontSize: '.82rem', color: 'var(--ts-text)' }}>
                    {search.tx.hash}
                  </p>
                  <div className="mt-3">
                    <div className="ts-row">
                      <span className="ts-row-k">Chain</span>
                      <span className="ts-row-v">{search.tx.chain || '—'}</span>
                    </div>
                    <div className="ts-row">
                      <span className="ts-row-k">From</span>
                      <span className="ts-row-v ts-mono">{search.tx.from || '—'}</span>
                    </div>
                    <div className="ts-row">
                      <span className="ts-row-k">To</span>
                      <span className="ts-row-v ts-mono">{search.tx.to || '—'}</span>
                    </div>
                  </div>
                  {search.tx.hash && search.tx.chain ? (
                    <Link
                      href={`/tx/${encodeURIComponent(search.tx.chain)}/${encodeURIComponent(search.tx.hash)}`}
                      className="ts-btn ts-btn-primary ts-btn-sm mt-4"
                    >
                      Open transaction
                    </Link>
                  ) : null}
                </section>
              ) : (
                <div className="ts-card p-8 text-center">
                  <p className="ts-eyebrow">Transaction not found</p>
                  <p className="ts-lede mx-auto mt-3 max-w-md">
                    We checked the major EVM chains and could not find that hash. Double-check it, or search the
                    wallet address instead.
                  </p>
                </div>
              )
            ) : null}

            {search.kind === 'wallets' ? (
              resultCount === 0 ? (
                <div className="ts-card p-8 text-center">
                  <p className="ts-eyebrow">No wallets matched</p>
                  <p className="ts-lede mx-auto mt-3 max-w-md">
                    Nothing came back for that term. Check the address, or try a specific chain.
                  </p>
                </div>
              ) : (
                <div className="grid gap-3">
                  <p className="ts-hint" style={{ marginTop: 0 }}>
                    {resultCount} result{resultCount === 1 ? '' : 's'}
                  </p>
                  {results.map((p) => {
                    const tone = trustTone(p.trustScore);
                    return (
                      <article className="ts-result" key={`${p.chain}-${p.address}`}>
                        <div className="ts-avatar">
                          <WalletAvatar address={p.address} avatarUrl={p.socials?.avatarUrl || null} size={52} alt="" />
                        </div>

                        <div className="ts-result-main">
                          <h3 className="ts-result-name">
                            {p.socials?.displayName || p.ens || shortAddress(p.address)}
                          </h3>
                          <p
                            className="ts-mono"
                            style={{ fontSize: '.72rem', color: 'var(--ts-muted)', margin: '3px 0 0' }}
                          >
                            {p.address}
                          </p>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <span className="ts-chip">{p.chain}</span>
                            {typeof p.trustScore === 'number' ? (
                              <span className="ts-score">
                                <b>{Math.round(p.trustScore)}</b>
                                <span>Trust</span>
                              </span>
                            ) : null}
                            {p.blacklisted ? (
                              <span className="ts-chip" data-tone="danger">
                                Blacklisted
                              </span>
                            ) : null}
                            {p.flagsCount ? (
                              <span className="ts-chip" data-tone="warn">
                                {p.flagsCount} flag{p.flagsCount === 1 ? '' : 's'}
                              </span>
                            ) : null}
                            {p.kycStatus === 'verified' ? (
                              <span className="ts-chip" data-tone="ok">
                                KYC verified
                              </span>
                            ) : null}
                            {tone ? (
                              <span className="ts-chip" data-tone={tone}>
                                {tone === 'ok' ? 'Trusted' : tone === 'warn' ? 'Mixed signals' : 'Low trust'}
                              </span>
                            ) : null}
                            {(p.statusTags || []).slice(0, 2).map((t) => (
                              <span className="ts-chip" key={t}>
                                {t}
                              </span>
                            ))}
                          </div>
                          {p.blacklisted && p.blacklistReason ? (
                            <p className="ts-error">{p.blacklistReason}</p>
                          ) : (p.flagsSummary || []).length ? (
                            <p className="ts-hint">{(p.flagsSummary || []).join(' · ')}</p>
                          ) : null}
                        </div>

                        <div className="ts-result-actions">
                          <Link
                            href={`/wallet/${encodeURIComponent(p.chain)}/${encodeURIComponent(p.address)}`}
                            className="ts-btn ts-btn-primary ts-btn-sm"
                          >
                            View profile
                          </Link>
                          <Link
                            href={`/wallet-analysis?chain=${encodeURIComponent(p.chain)}&address=${encodeURIComponent(p.address)}`}
                            className="ts-btn ts-btn-sm"
                          >
                            Analyse
                          </Link>
                          <button type="button" className="ts-btn ts-btn-sm" onClick={() => reportFromResult(p)}>
                            Report
                          </button>
                        </div>
                      </article>
                    );
                  })}
                </div>
              )
            ) : null}

            {search.kind === 'idle' ? (
              <div className="ts-card p-8 text-center sm:p-10">
                <p className="ts-eyebrow">Start with an address</p>
                <p className="ts-lede mx-auto mt-3 max-w-lg">
                  Searching is read-only and anonymous — nothing is recorded against the wallet you look up.
                </p>
              </div>
            ) : null}
          </div>

          {/* ── Report intake ──────────────────────────────────────────── */}
          <details
            className="ts-card ts-details mt-6 p-5 sm:p-7"
            ref={reportRef}
            open={reportOpen}
            onToggle={(e) => setReportOpen((e.currentTarget as HTMLDetailsElement).open)}
            id="report"
          >
            <summary>
              <span className="min-w-0">
                <span className="ts-eyebrow">Report an address</span>
                <span className="ts-lede mt-2 block">
                  Seen a scam, or a wallet impersonating a service? File a report and we&apos;ll route it into
                  the reputation system.
                </span>
              </span>
            </summary>

            <div className="mt-5">
              <div className="ts-field">
                <label className="ts-label" htmlFor="my-wallet">
                  My wallet (owner)
                </label>
                <input
                  id="my-wallet"
                  value={myWallet}
                  onChange={(e) => setMyWallet(e.target.value)}
                  className="ts-input ts-mono"
                  placeholder="0x1234… (address you control)"
                  autoComplete="off"
                  spellCheck={false}
                />
              </div>

              <div className="mt-4 grid gap-4 md:grid-cols-2">
                <div>
                  <label className="ts-label" htmlFor="report-chain">
                    Chain
                  </label>
                  <select id="report-chain" value={chain} onChange={(e) => setChain(e.target.value)} className="ts-select">
                    {CHAINS.map((c) => (
                      <option key={c.value} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="ts-label" htmlFor="suspect-address">
                    Receiving address
                  </label>
                  <input
                    id="suspect-address"
                    value={suspect}
                    onChange={(e) => setSuspect(e.target.value)}
                    className="ts-input ts-mono"
                    placeholder="Address you want to check or flag"
                    autoComplete="off"
                    spellCheck={false}
                  />
                </div>
              </div>

              <div className="mt-5">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <label className="ts-label" style={{ marginBottom: 0 }}>
                    Provider / exchange
                  </label>
                  <button
                    type="button"
                    className="ts-btn ts-btn-sm"
                    onClick={() => {
                      const val = !showProviderFeatures;
                      if (!val) setProvider(null);
                      setShowProviderFeatures(val);
                      try {
                        localStorage.setItem('showProviderFeatures', String(val));
                      } catch {
                        /* ignore */
                      }
                    }}
                  >
                    {showProviderFeatures ? 'Hide' : 'Show'}
                  </button>
                </div>
                {showProviderFeatures ? (
                  <div className="mt-3 grid gap-3">
                    <ProviderSelect
                      value={provider}
                      onChange={(p) => {
                        // A provider with no _id means "add this to the directory".
                        if (p && !('_id' in p) && p.name) {
                          setPendingProviderName(p.name);
                          setAddingProvider(true);
                        } else {
                          setProvider(p as ProviderType | null);
                        }
                      }}
                    />
                    <button
                      type="button"
                      onClick={() => setAddingProvider(true)}
                      className="ts-btn ts-btn-sm"
                      style={{ width: 'max-content' }}
                      aria-haspopup="dialog"
                    >
                      + Add provider to directory
                    </button>
                  </div>
                ) : (
                  <p className="ts-hint">Optional attribution layer — turn it on to link a known exchange.</p>
                )}
              </div>

              {/* Four actions, four buttons. The hero used to repeat two of them. */}
              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:flex-wrap">
                <button type="button" onClick={openReport} className="ts-btn ts-btn-primary">
                  Flag / report address
                </button>
                <button type="button" onClick={analyzeAddress} className="ts-btn">
                  Analyse address
                </button>
                <button type="button" onClick={signAndVerify} className="ts-btn">
                  {authenticated ? 'Verified wallet session' : 'Sign & verify my wallet'}
                </button>
                <button type="button" onClick={resetIntake} className="ts-btn">
                  Reset
                </button>
              </div>

              {statusMessage ? (
                <p className="ts-hint" role="status">
                  {statusMessage}
                </p>
              ) : null}
              {errorMessage ? (
                <p className="ts-error" role="alert">
                  {errorMessage}
                </p>
              ) : null}

              {openReportModal ? (
                <div className="mt-5" style={{ border: '1px solid var(--ts-border)', borderRadius: 18, padding: 16 }}>
                  <ReportModal
                    inline
                    chain={chain}
                    myWallet={myWallet}
                    provider={provider ?? undefined}
                    suspect={suspect}
                    onClose={() => setOpenReportModal(false)}
                    onSubmitted={(report) => {
                      setSuspect('');
                      setOpenReportModal(false);
                      try {
                        const rep = (report as Record<string, unknown> | null) || null;
                        const rc = (rep && typeof rep.chain === 'string' ? rep.chain : chain) || 'ethereum';
                        const address = rep && typeof rep.suspectAddress === 'string' ? rep.suspectAddress : suspect;
                        if (address) router.push(`/wallet/${rc}/${address}`);
                      } catch {
                        // ignore navigation errors
                      }
                    }}
                  />
                </div>
              ) : null}

              {addingProvider ? (
                <div className="mt-5" style={{ border: '1px solid var(--ts-border)', borderRadius: 18, padding: 16 }}>
                  <AddProviderModal
                    inline
                    initialName={pendingProviderName || provider?.name || ''}
                    onClose={() => {
                      setAddingProvider(false);
                      setPendingProviderName(null);
                    }}
                    onCreated={(p) => {
                      setProvider(p as ProviderType);
                      setAddingProvider(false);
                      setPendingProviderName(null);
                    }}
                  />
                </div>
              ) : null}
            </div>
          </details>

          {/* ── Playbook ───────────────────────────────────────────────── */}
          <section className="ts-card mt-6 p-5 sm:p-6">
            <p className="ts-eyebrow">How reports are used</p>
            <ol className="ts-lede mt-3" style={{ paddingLeft: '1.15rem', display: 'grid', gap: '.4rem' }}>
              <li>Search the address to see what the community already knows.</li>
              <li>Verify your own wallet so your report carries weight.</li>
              <li>Optionally attribute a known provider or exchange.</li>
              <li>File the report — it feeds the wallet&apos;s trust and risk score.</li>
            </ol>
          </section>
        </div>
      </main>

      <Footer />
    </div>
  );
}
